import { useState, useEffect, useRef } from 'react';

/**
 * Hook to animate text typing character by character
 * @param {string} text - The text to animate
 * @param {number} speed - Typing speed in milliseconds per character (default: 30)
 * @param {boolean} enabled - Whether animation is enabled (default: true)
 * @returns {string} - The animated text
 */
export function useTypingAnimation(text, speed = 30, enabled = true) {
  const [displayedText, setDisplayedText] = useState(text || '');
  const timeoutRef = useRef(null);
  const currentIndexRef = useRef(0);
  const previousTextRef = useRef(text || '');

  useEffect(() => {
    // If text changed, reset and start animation
    if (text !== previousTextRef.current) {
      previousTextRef.current = text;
      
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      if (!enabled || !text) {
        setDisplayedText(text || '');
        currentIndexRef.current = text?.length || 0;
        return;
      }

      // Reset displayed text and index
      setDisplayedText('');
      currentIndexRef.current = 0;
      
      // Animate typing
      const animate = () => {
        if (currentIndexRef.current < text.length) {
          setDisplayedText(text.substring(0, currentIndexRef.current + 1));
          currentIndexRef.current++;
          timeoutRef.current = setTimeout(animate, speed);
        } else {
          setDisplayedText(text);
          currentIndexRef.current = text.length;
        }
      };

      // Start animation after a small delay
      timeoutRef.current = setTimeout(animate, 50);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [text, speed, enabled]);

  return displayedText;
}

